/**
 * Voice Channel Window Component
 *
 * Floating window for channel voice sessions with LiveKit
 * Shows participants, controls, and screen share
 */

import React from 'react';
import { useLiveKit } from '@/contexts/LiveKitContext';
import { useUserSettings } from '@/hooks/useUserSettings';
import { Mic, MicOff, Volume2, VolumeX, Monitor, PhoneOff } from 'lucide-react';
import { ScreenShareView } from './ScreenShareView';

// Map of keyboard codes to display labels
const KEY_LABELS: Record<string, string> = {
  Space: 'SPACE',
  ControlLeft: 'LEFT CTRL',
  ControlRight: 'RIGHT CTRL',
  ShiftLeft: 'LEFT SHIFT',
  ShiftRight: 'RIGHT SHIFT',
  AltLeft: 'LEFT ALT',
  AltRight: 'RIGHT ALT',
  KeyV: 'V',
  KeyT: 'T',
};

export const VoiceChannelWindow: React.FC = () => {
  const { state, leaveChannelVoice, leaveVoiceRoom, toggleMute, toggleDeafen, toggleScreenShare } = useLiveKit();
  const { data: userSettings } = useUserSettings();

  // Don't render if not in a voice session
  if (!state.isConnected || (!state.channelId && !state.voiceRoomId)) {
    return null;
  }

  const isVoiceRoom = !!state.voiceRoomId;
  const handleLeave = isVoiceRoom ? leaveVoiceRoom : leaveChannelVoice;

  return (
    <div className="fixed bottom-4 right-4 bg-gray-800 text-white rounded-lg shadow-xl p-4 w-80 z-50 animate-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{isVoiceRoom ? 'Гласова стая' : 'Гласов канал'}</h3>
        <button
          onClick={handleLeave}
          className="text-gray-400 hover:text-white transition-colors"
          title="Напусни гласовия канал"
          aria-label="Напусни гласовия канал"
        >
          ✕
        </button>
      </div>

      {/* Connection status */}
      {state.isConnecting && (
        <div className="mb-4 text-yellow-400 text-sm flex items-center space-x-2">
          <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
          <span>Connecting...</span>
        </div>
      )}

      {/* Error message */}
      {state.error && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-600 rounded text-sm text-red-200">
          {state.error}
        </div>
      )}

      {/* Participants */}
      <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
        {state.participants.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">
            Waiting for participants...
          </p>
        ) : (
          state.participants.map((participant) => (
            <div
              key={participant.userId}
              className="flex items-center space-x-3 p-2 bg-gray-700 rounded"
            >
              {/* Avatar placeholder with speaking indicator and connection quality ring */}
              <div className="relative">
                <div
                  className={`w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white font-semibold ${
                    participant.isSpeaking
                      ? 'ring-2 ring-green-500'
                      : 'ring-2 ring-green-400/30'
                  }`}
                  title={participant.isSpeaking ? 'Speaking' : 'Connected'}
                >
                  {participant.displayName.charAt(0).toUpperCase()}
                </div>
                {participant.isSpeaking && (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-700 animate-pulse" />
                )}
              </div>

              {/* Name and status */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {participant.displayName}
                </p>
                <div className="flex items-center space-x-1 text-xs text-gray-400">
                  {participant.isMuted ? (
                    <span title="Изключен микрофон" className="text-red-400">
                      <MicOff className="w-3 h-3 inline" />
                    </span>
                  ) : (
                    <span title="Свързан" className="text-green-400">
                      <Mic className="w-3 h-3 inline" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center space-x-4 mb-4">
        {/* Mute button */}
        <button
          onClick={toggleMute}
          disabled={state.isDeafened}
          className={`p-3 rounded-full transition-colors ${
            state.isMuted
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-gray-600 hover:bg-gray-700'
          } ${state.isDeafened ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={state.isMuted ? 'Включи микрофон' : 'Изключи микрофон'}
          aria-label={state.isMuted ? 'Unmute microphone' : 'Mute microphone'}
        >
          {state.isMuted ? (
            <MicOff className="w-5 h-5 text-white" />
          ) : (
            <Mic className="w-5 h-5 text-white" />
          )}
        </button>

        {/* Deafen button */}
        <button
          onClick={toggleDeafen}
          className={`p-3 rounded-full transition-colors ${
            state.isDeafened
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-gray-600 hover:bg-gray-700'
          }`}
          title={state.isDeafened ? 'Включи звука' : 'Изключи звука'}
          aria-label={state.isDeafened ? 'Undeafen' : 'Deafen'}
        >
          {state.isDeafened ? (
            <VolumeX className="w-5 h-5 text-white" />
          ) : (
            <Volume2 className="w-5 h-5 text-white" />
          )}
        </button>

        {/* Screen share button */}
        <button
          onClick={toggleScreenShare}
          className={`p-3 rounded-full transition-colors ${
            state.isScreenSharing
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-600 hover:bg-gray-700'
          }`}
          title={state.isScreenSharing ? 'Stop sharing' : 'Share screen'}
          aria-label={state.isScreenSharing ? 'Stop screen share' : 'Start screen share'}
        >
          <Monitor className="w-5 h-5 text-white" />
        </button>

        {/* Disconnect button */}
        <button
          onClick={handleLeave}
          className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
          title="Напусни гласовия канал"
          aria-label="Напусни гласовия канал"
        >
          <PhoneOff className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Remote Screen Shares */}
      {state.screenShares.length > 0 && (
        <div className="mb-4 space-y-2">
          <h4 className="text-sm font-medium text-gray-300">Споделени екрани:</h4>
          {state.screenShares.map((share) => (
            <ScreenShareView
              key={share.participantId}
              participantId={share.participantId}
              participantName={share.participantName}
              track={share.track}
            />
          ))}
        </div>
      )}

      {/* Connection info */}
      <div className="pt-4 border-t border-gray-700 space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            {state.participants.length}{' '}
            {state.participants.length === 1 ? 'участник' : 'участници'}
          </span>
          <div className="flex gap-2">
            {state.isDeafened && <span className="text-red-400">Заглушен</span>}
            {state.isMuted && !state.isDeafened && <span className="text-red-400">Без звук</span>}
            {state.isScreenSharing && <span className="text-blue-400">Споделяне</span>}
            {state.screenShares.length > 0 && (
              <span className="text-blue-400">{state.screenShares.length} екран(а)</span>
            )}
          </div>
        </div>

        {/* Push-to-talk indicator */}
        {userSettings?.push_to_talk_enabled && (
          <div className="flex items-center justify-center gap-2 p-2 bg-blue-900/30 border border-blue-600/50 rounded text-xs text-blue-300">
            <span>🎤 Push to Talk:</span>
            <kbd className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white font-semibold">
              {KEY_LABELS[userSettings.push_to_talk_key] || userSettings.push_to_talk_key}
            </kbd>
          </div>
        )}
      </div>
    </div>
  );
};
